import React, { useState } from 'react';
import { Node, Edge } from 'reactflow';
import { 
  Play, Pause, StepForward, RotateCcw, Bug, Terminal, 
  CheckCircle, AlertCircle, XCircle, Clock, ChevronRight,
  ChevronDown, Activity, Copy, Download, Trash2, Eye, EyeOff
} from 'lucide-react';

interface ExecutionStep {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: 'pending' | 'running' | 'success' | 'error' | 'skipped';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  input?: any;
  output?: any;
  error?: string;
  logs?: string[];
}

interface Variable {
  name: string;
  value: any;
  scope: 'global' | 'flow' | 'local';
}

interface DebugPanelProps {
  nodes: Node[];
  edges: Edge[];
  isRunning?: boolean;
  isPaused?: boolean;
  onExecute?: () => void;
  onPause?: () => void;
  onStep?: () => void;
  onStop?: () => void;
  onReset?: () => void;
  onBreakpointToggle?: (nodeId: string) => void;
  executionSteps?: ExecutionStep[];
  currentStep?: number;
  variables?: Variable[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({
  nodes,
  edges,
  isRunning = false,
  isPaused = false,
  onExecute = () => {},
  onPause = () => {},
  onStep = () => {},
  onStop = () => {},
  onReset = () => {},
  onBreakpointToggle = () => {},
  executionSteps = [],
  currentStep = -1,
  variables = [],
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [activeTab, setActiveTab] = useState<'execution' | 'variables' | 'logs'>('execution');
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [breakpoints] = useState<Set<string>>(new Set());

  const toggleStepExpansion = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const getStatusIcon = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={14} color="#10B981" />;
      case 'error':
        return <XCircle size={14} color="#EF4444" />;
      case 'running':
        return <Activity size={14} color="#F59E0B" className="animate-pulse" />;
      case 'pending':
        return <Clock size={14} color="#6B7280" />;
      case 'skipped':
        return <ChevronRight size={14} color="#9CA3AF" />;
      default:
        return null;
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatValue = (value: any): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  };

  const exportLogs = () => {
    const logs = executionSteps.map(step => ({
      node: step.nodeName,
      type: step.nodeType,
      status: step.status,
      duration: step.duration,
      input: step.input,
      output: step.output,
      error: step.error,
      logs: step.logs
    }));
    
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-log-${Date.now()}.json`;
    a.click();
  };

  const clearLogs = () => {
    if (window.confirm('Limpar todos os logs de execução?')) {
      // Clear logs logic
    }
  };

  const filteredSteps = executionSteps.filter(step => {
    if (showOnlyErrors && step.status !== 'error') return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        step.nodeName.toLowerCase().includes(query) ||
        step.nodeType.toLowerCase().includes(query) ||
        step.status.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const totalDuration = executionSteps.reduce((sum, step) => sum + (step.duration || 0), 0);
  const successCount = executionSteps.filter(s => s.status === 'success').length;
  const errorCount = executionSteps.filter(s => s.status === 'error').length;

  if (isCollapsed) {
    return (
      <div
        className="debug-panel-collapsed"
        onClick={onToggleCollapse}
        style={{
          width: '48px',
          height: '100%',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderLeft: '1px solid #d1d5db'
        }}
      >
        <Bug size={20} color="#6b7280" />
      </div>
    );
  }

  return (
    <div style={{
      width: '400px',
      height: '100%',
      background: '#ffffff',
      borderLeft: '1px solid #d1d5db',
      display: 'flex',
      flexDirection: 'column',
      color: '#374151'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Bug size={16} />
            Debug & Teste
          </h3>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#6b7280'
              }}
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* Control Buttons */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '12px'
        }}>
          <button
            onClick={isRunning ? onPause : onExecute}
            disabled={isRunning && isPaused}
            style={{
              flex: 1,
              padding: '8px',
              background: isRunning ? '#EF4444' : '#10B981',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              opacity: (isRunning && isPaused) ? 0.5 : 1
            }}
          >
            {isRunning ? (
              <>
                <Pause size={14} />
                Pausar
              </>
            ) : (
              <>
                <Play size={14} />
                Executar
              </>
            )}
          </button>

          <button
            onClick={onStep}
            disabled={!isPaused}
            style={{
              padding: '8px 12px',
              background: '#3B82F6',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: isPaused ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: isPaused ? 1 : 0.5
            }}
          >
            <StepForward size={14} />
            Step
          </button>

          <button
            onClick={onReset}
            style={{
              padding: '8px 12px',
              background: '#6B7280',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RotateCcw size={14} />
          </button>
        </div>

        {/* Status Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '8px',
          padding: '8px',
          background: '#f9fafb',
          borderRadius: '6px',
          fontSize: '11px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <CheckCircle size={12} color="#10B981" />
            <span>Sucesso: {successCount}</span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <XCircle size={12} color="#EF4444" />
            <span>Erros: {errorCount}</span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Clock size={12} color="#F59E0B" />
            <span>{formatDuration(totalDuration)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb'
      }}>
        {(['execution', 'variables', 'logs'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: '8px',
              background: activeTab === tab ? '#3B82F6' : 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #3B82F6' : 'none',
              color: activeTab === tab ? '#ffffff' : '#6b7280',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              textTransform: 'capitalize'
            }}
          >
            {tab === 'execution' && 'Execução'}
            {tab === 'variables' && 'Variáveis'}
            {tab === 'logs' && 'Logs'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {activeTab === 'execution' && (
          <>
            {/* Search and Filters */}
            <div style={{
              marginBottom: '12px',
              display: 'flex',
              gap: '8px'
            }}>
              <input
                type="text"
                placeholder="Buscar nós..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
              <button
                onClick={() => setShowOnlyErrors(!showOnlyErrors)}
                style={{
                  padding: '6px 10px',
                  background: showOnlyErrors ? '#EF4444' : '#f1f5f9',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: showOnlyErrors ? '#ffffff' : '#6b7280',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <AlertCircle size={14} />
              </button>
            </div>

            {/* Execution Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredSteps.map((step, index) => (
                <div
                  key={`${step.nodeId}-${index}`}
                  style={{
                    background: '#f9fafb',
                    border: `1px solid ${
                      step.status === 'error' ? '#EF444466' :
                      step.status === 'success' ? '#10B98166' :
                      step.status === 'running' ? '#F59E0B66' :
                      '#d1d5db'
                    }`,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    onClick={() => toggleStepExpansion(step.nodeId)}
                    style={{
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    {expandedSteps.has(step.nodeId) ?
                      <ChevronDown size={14} /> :
                      <ChevronRight size={14} />
                    }
                    {getStatusIcon(step.status)}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        {step.nodeName}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#6b7280'
                      }}>
                        {step.nodeType} • {formatDuration(step.duration)}
                      </div>
                    </div>
                    {breakpoints.has(step.nodeId) && (
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#EF4444'
                        }}
                      />
                    )}
                  </div>

                  {expandedSteps.has(step.nodeId) && (
                    <div style={{
                      padding: '10px',
                      borderTop: '1px solid #d1d5db',
                      fontSize: '11px'
                    }}>
                      {step.input && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ color: '#6b7280', marginBottom: '4px' }}>Input:</div>
                          <pre style={{
                            margin: 0,
                            padding: '6px',
                            background: '#ffffff',
                            borderRadius: '4px',
                            overflow: 'auto',
                            maxHeight: '100px'
                          }}>
                            {formatValue(step.input)}
                          </pre>
                        </div>
                      )}

                      {step.output && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ color: '#6b7280', marginBottom: '4px' }}>Output:</div>
                          <pre style={{
                            margin: 0,
                            padding: '6px',
                            background: '#ffffff',
                            borderRadius: '4px',
                            overflow: 'auto',
                            maxHeight: '100px'
                          }}>
                            {formatValue(step.output)}
                          </pre>
                        </div>
                      )}

                      {step.error && (
                        <div>
                          <div style={{ color: '#EF4444', marginBottom: '4px' }}>Error:</div>
                          <pre style={{
                            margin: 0,
                            padding: '6px',
                            background: '#7F1D1D',
                            borderRadius: '4px',
                            color: '#FCA5A5'
                          }}>
                            {step.error}
                          </pre>
                        </div>
                      )}

                      {step.logs && step.logs.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ color: '#6b7280', marginBottom: '4px' }}>Logs:</div>
                          {step.logs.map((log, i) => (
                            <div
                              key={i}
                              style={{
                                padding: '4px 6px',
                                background: '#ffffff',
                                borderRadius: '4px',
                                marginBottom: '2px',
                                fontFamily: 'monospace',
                                fontSize: '10px'
                              }}
                            >
                              {log}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'variables' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {['global', 'flow', 'local'].map(scope => {
              const scopeVars = variables.filter(v => v.scope === scope);
              if (scopeVars.length === 0) return null;

              return (
                <div key={scope}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    marginBottom: '8px',
                    textTransform: 'capitalize'
                  }}>
                    {scope === 'global' ? 'Globais' : scope === 'flow' ? 'Fluxo' : 'Locais'}
                  </div>
                  {scopeVars.map(variable => (
                    <div
                      key={`${variable.scope}-${variable.name}`}
                      style={{
                        padding: '8px',
                        background: '#f9fafb',
                        borderRadius: '6px',
                        marginBottom: '6px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#3B82F6'
                        }}>
                          {variable.name}
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(formatValue(variable.value))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '2px'
                          }}
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                      <pre style={{
                        margin: 0,
                        padding: '6px',
                        background: '#ffffff',
                        borderRadius: '4px',
                        fontSize: '10px',
                        overflow: 'auto',
                        maxHeight: '80px'
                      }}>
                        {formatValue(variable.value)}
                      </pre>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'logs' && (
          <div>
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <button
                onClick={() => setAutoScroll(!autoScroll)}
                style={{
                  padding: '6px 10px',
                  background: autoScroll ? '#3B82F6' : '#f1f5f9',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: autoScroll ? '#ffffff' : '#6b7280',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {autoScroll ? <Eye size={12} /> : <EyeOff size={12} />}
                Auto-scroll
              </button>
              <button
                onClick={exportLogs}
                style={{
                  padding: '6px 10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#6b7280',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <Download size={12} />
              </button>
              <button
                onClick={clearLogs}
                style={{
                  padding: '6px 10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#6b7280',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={12} />
              </button>
            </div>

            <div style={{
              padding: '8px',
              background: '#f9fafb',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '11px',
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              <Terminal size={14} style={{ marginBottom: '8px', color: '#10B981' }} />
              {executionSteps.map((step, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#6b7280' }}>
                    [{new Date(step.startTime || '').toLocaleTimeString()}]
                  </span>
                  {' '}
                  <span style={{
                    color: step.status === 'error' ? '#EF4444' :
                           step.status === 'success' ? '#10B981' :
                           '#F59E0B'
                  }}>
                    [{step.status.toUpperCase()}]
                  </span>
                  {' '}
                  <span style={{ color: '#374151' }}>
                    {step.nodeName}: {step.output ? JSON.stringify(step.output) : 'void'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebugPanel;
